/**
 * Bank Topology Controller — Drill-Down Navigator
 * Page controller for the 5-level Bank Service Topology visualization
 *
 * Sprint 34: Drill-down navigation replaces expand/collapse.
 * - focusNode tracks the node currently displayed at center
 * - Single-click drills into a child (if it has children)
 * - Breadcrumb segments are clickable to navigate back up
 * - Double-click opens a pop-out detail modal with X to close
 *
 * Following OOA/OOD principles:
 * - Template Method Pattern: Extends BaseController
 * - Mediator Pattern: Coordinates between data service and visualizers
 * - Single Responsibility: Page-level orchestration only
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import { TopologyDataService } from '../../topology/topology-data-service';
import { RadialTopologyVisualizer } from '../../topology/visualizer/radial-topology-visualizer';
import { FullTopologyVisualizer } from '../../topology/visualizer/full-topology-visualizer';
import { ProcessFlowVisualizer } from '../../topology/visualizer/process-flow-visualizer';
import { TopologyNode, DEFAULT_LEVEL_COLORS } from '../../topology/topology-types';
/**
 * View mode for Bank Topology page (Sprint 36)
 */
type TopologyViewMode = 'drill-down' | 'full';

/** localStorage key for persisting view mode preference */
const TOPOLOGY_VIEW_MODE_KEY = 'bank-topology-view-mode';

/**
 * BankTopologyController — Manages the Bank Service Topology page
 * with drill-down navigation model.
 */
export class BankTopologyController extends BaseController {
    private dataService: TopologyDataService;
    private topologyVisualizer: RadialTopologyVisualizer;
    private fullTopologyVisualizer: FullTopologyVisualizer;
    private flowVisualizer: ProcessFlowVisualizer;
    private rootNode: TopologyNode | null = null;
    private focusNode: TopologyNode | null = null;
    private selectedNode: TopologyNode | null = null;

    /** Current view mode: drill-down (radial) or full (constellation) */
    private viewMode: TopologyViewMode = 'drill-down';

    constructor(services: ControllerServices) {
        super(services);
        this.dataService = TopologyDataService.getInstance();
        this.topologyVisualizer = new RadialTopologyVisualizer();
        this.fullTopologyVisualizer = new FullTopologyVisualizer();
        this.flowVisualizer = new ProcessFlowVisualizer();

        // Restore view mode from localStorage
        const savedMode = localStorage.getItem(TOPOLOGY_VIEW_MODE_KEY);
        if (savedMode === 'full' || savedMode === 'drill-down') {
            this.viewMode = savedMode;
        }
    }

    getPageName(): PageName {
        return 'bank-topology' as PageName;
    }

    protected async doInit(): Promise<void> {
        this.setupViewToggle();
        await this.loadTopology();
    }

    protected setupEventListeners(): void {
        // Refresh button
        const refreshBtn = document.getElementById('btn-refresh-topology');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => this.loadTopology());
        }

        // Export button
        const exportBtn = document.getElementById('btn-export-topology');
        if (exportBtn) {
            exportBtn.addEventListener('click', () => this.exportTopologyPNG());
        }

        // Modal close button
        const closeBtn = document.getElementById('btn-close-detail-modal');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeDetailModal());
        }

        // Close modal on backdrop click
        const modal = document.getElementById('topology-detail-modal');
        if (modal) {
            modal.addEventListener('click', (e) => {
                if (e.target === modal) this.closeDetailModal();
            });
        }

        // Reset full view button (Sprint 36)
        const resetBtn = document.getElementById('btn-reset-full-view');
        if (resetBtn) {
            resetBtn.addEventListener('click', () => this.fullTopologyVisualizer.resetView());
        }
    }

    // ----------------------------------------------------------------
    // View mode toggle (Sprint 36)
    // ----------------------------------------------------------------

    /**
     * Set up view toggle button event listeners
     */
    private setupViewToggle(): void {
        // All view toggle buttons (in both containers)
        const viewButtons = document.querySelectorAll<HTMLElement>('.topology-view-btn');
        viewButtons.forEach((btn) => {
            btn.addEventListener('click', () => {
                const mode = btn.getAttribute('data-view') as TopologyViewMode;
                if (mode) {
                    this.switchViewMode(mode);
                }
            });
        });

        // Apply initial view mode
        this.updateViewToggleUI(this.viewMode);
        this.applyViewMode(this.viewMode);
    }

    /**
     * Switch between drill-down and full view modes
     */
    private switchViewMode(mode: TopologyViewMode): void {
        if (this.viewMode === mode) return;

        console.log('[BankTopology] switchViewMode:', mode);
        this.viewMode = mode;

        // Persist preference
        localStorage.setItem(TOPOLOGY_VIEW_MODE_KEY, mode);

        // Update UI and apply view
        this.updateViewToggleUI(mode);
        this.applyViewMode(mode);
    }

    /**
     * Apply the view mode (show/hide containers)
     */
    private applyViewMode(mode: TopologyViewMode): void {
        const drillDownContainer = document.getElementById('topology-drill-down-container');
        const fullViewContainer = document.getElementById('topology-full-view-container');
        const breadcrumb = document.getElementById('topology-breadcrumb')?.parentElement;

        if (mode === 'drill-down') {
            drillDownContainer?.classList.remove('hidden');
            fullViewContainer?.classList.add('hidden');
            breadcrumb?.classList.remove('hidden');
        } else {
            drillDownContainer?.classList.add('hidden');
            fullViewContainer?.classList.remove('hidden');
            breadcrumb?.classList.add('hidden');

            // Render full view if we have data
            if (this.rootNode) {
                this.renderFullView();
            }
        }
    }

    /**
     * Update the visual state of all view toggle buttons
     */
    private updateViewToggleUI(mode: TopologyViewMode): void {
        const viewButtons = document.querySelectorAll<HTMLElement>('.topology-view-btn');
        viewButtons.forEach((btn) => {
            const btnMode = btn.getAttribute('data-view');
            if (btnMode === mode) {
                btn.classList.remove('bg-slate-800', 'bg-white', 'text-slate-400', 'text-slate-500', 'hover:text-white', 'hover:text-slate-700');
                btn.classList.add('bg-slate-600', 'text-white');
            } else {
                btn.classList.remove('bg-slate-600', 'text-white');
                // Check if button is in the light or dark container
                if (btn.closest('#topology-full-view-container')) {
                    btn.classList.add('bg-white', 'text-slate-500', 'hover:text-slate-700');
                } else {
                    btn.classList.add('bg-slate-800', 'text-slate-400', 'hover:text-white');
                }
            }
        });
    }

    /**
     * Render the full constellation view
     */
    private async renderFullView(): Promise<void> {
        if (!this.rootNode) return;

        const container = document.getElementById('topology-full-svg-container');
        if (!container) return;

        this.fullTopologyVisualizer.initialize(container);
        this.fullTopologyVisualizer.onNodeClick((node) => this.handleFullViewNodeClick(node));
        this.fullTopologyVisualizer.onNodeDoubleClick((node) => this.handleNodeDoubleClick(node));

        this.fullTopologyVisualizer.render(this.rootNode);
    }

    /**
     * Handle node click in full view - show detail modal
     */
    private handleFullViewNodeClick(node: TopologyNode): void {
        this.selectedNode = node;
        // In full view, single click shows detail (no drill-down)
        this.showDetailModal(node);
    }

    /**
     * Load topology data and render the visualization
     */
    public async loadTopology(): Promise<void> {
        const container = document.getElementById('topology-svg-container');

        if (!container) return;

        // Show loading
        container.innerHTML = `
            <div class="flex items-center justify-center h-64">
                <div class="text-center">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                    <p class="text-gray-500">Loading topology data...</p>
                </div>
            </div>
        `;

        try {
            // Load tree
            this.rootNode = await this.dataService.loadTopologyTree();

            // Auto-seed: if no BUs exist or BUs exist but no process flows, seed everything
            if (this.rootNode.children.length === 0 || !this.hasProcessFlows(this.rootNode)) {
                console.log('Topology data incomplete — auto-seeding bank data...');
                await this.dataService.seedBankData();
                this.rootNode = await this.dataService.loadTopologyTree();
            }

            // Recursively expand all nodes so children are accessible for drill-down
            this.expandAllNodes(this.rootNode);

            // Start with root as focus
            this.focusNode = this.rootNode;

            // Initialize visualizer
            this.topologyVisualizer.initialize(container);
            this.topologyVisualizer.onNodeClick((node) => this.handleNodeClick(node));
            this.topologyVisualizer.onNodeDoubleClick((node) => this.handleNodeDoubleClick(node));

            // Render
            if (this.rootNode.children.length === 0) {
                container.innerHTML = `
                    <div class="flex items-center justify-center h-64 text-center">
                        <div>
                            <i data-feather="git-branch" class="w-12 h-12 mx-auto mb-3 text-gray-300"></i>
                            <p class="text-gray-500 mb-2">No topology data found</p>
                            <p class="text-sm text-gray-400">Visit Business Capabilities to set up the topology hierarchy first.</p>
                        </div>
                    </div>
                `;
                this.refreshIcons();
            } else {
                this.topologyVisualizer.render(this.focusNode);
                this.updateBreadcrumb();

                // Also render full view if that's the current mode (Sprint 36)
                if (this.viewMode === 'full') {
                    await this.renderFullView();
                }
            }

        } catch (error) {
            console.error('Failed to load topology:', error);
            container.innerHTML = `
                <div class="flex items-center justify-center h-64 text-center">
                    <div>
                        <i data-feather="alert-triangle" class="w-12 h-12 mx-auto mb-3 text-red-400"></i>
                        <p class="text-red-500">Failed to load topology data</p>
                        <p class="text-sm text-gray-400">${String(error)}</p>
                    </div>
                </div>
            `;
            this.refreshIcons();
        }
    }

    /**
     * Handle node single-click — drill down if node has children
     */
    public handleNodeClick(node: TopologyNode): void {
        this.selectedNode = node;

        // If node has children, drill down into it
        if (node.children.length > 0) {
            this.focusNode = node;
            this.topologyVisualizer.render(this.focusNode);
            this.updateBreadcrumb();
        }
    }

    /**
     * Handle node double-click — show detail modal with process flow if applicable
     */
    public handleNodeDoubleClick(node: TopologyNode): void {
        this.selectedNode = node;

        // Show detail in the pop-out modal
        this.showDetailModal(node);

        // If it's a process flow, load and show the flow in the modal
        if (node.type === 'process-flow') {
            this.showProcessFlowInModal(node.id);
        }
    }

    /**
     * Open the detail modal and populate it with node info
     */
    private showDetailModal(node: TopologyNode): void {
        const modal = document.getElementById('topology-detail-modal');
        const panel = document.getElementById('topology-detail-panel');
        const flowPanel = document.getElementById('topology-flow-panel-modal');

        if (!modal || !panel) return;

        // Populate detail content
        this.renderNodeDetail(panel, node);

        // Hide flow panel initially
        if (flowPanel) {
            flowPanel.classList.add('hidden');
            flowPanel.innerHTML = '';
        }

        // Show modal
        modal.classList.remove('hidden');
    }

    /**
     * Close the detail modal
     */
    private closeDetailModal(): void {
        const modal = document.getElementById('topology-detail-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    }

    /**
     * Show process flow detail inside the modal
     */
    private async showProcessFlowInModal(flowId: string): Promise<void> {
        const flowPanel = document.getElementById('topology-flow-panel-modal');
        if (!flowPanel) return;

        flowPanel.innerHTML = `
            <div class="text-center py-4">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p class="text-gray-500 text-sm mt-2">Loading process flow...</p>
            </div>
        `;
        flowPanel.classList.remove('hidden');

        try {
            const steps = await this.dataService.getProcessSteps(flowId);

            const flowNode = this.selectedNode;
            if (!flowNode) return;

            const flowData = {
                processFlowId: flowId,
                name: flowNode.label,
                description: flowNode.metadata?.description || '',
                businessUnitId: '',
                capabilityId: '',
                status: (flowNode.metadata?.status || 'Draft') as any,
                ownerRole: flowNode.metadata?.owner_role || '',
                totalSlaHours: flowNode.metadata?.total_sla_hours
                    ? parseFloat(flowNode.metadata.total_sla_hours) : undefined,
                metadata: flowNode.metadata || {},
            };

            this.flowVisualizer.initialize(flowPanel);
            this.flowVisualizer.onHandoffClick((target) => this.navigateToHandoff(target));
            this.flowVisualizer.renderFlow(flowData, steps);
            this.refreshIcons();

        } catch (error) {
            console.error('Failed to load process flow:', error);
            flowPanel.innerHTML = `
                <div class="text-center py-4 text-red-500">
                    Failed to load process flow: ${String(error)}
                </div>
            `;
        }
    }

    /**
     * Seed bank data
     */
    public async seedData(): Promise<void> {
        const seedBtn = document.getElementById('btn-seed-topology-data');
        const originalText = seedBtn?.innerHTML || '';

        if (seedBtn) {
            seedBtn.setAttribute('disabled', 'true');
            seedBtn.innerHTML = `
                <svg class="animate-spin h-4 w-4 inline-block mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Seeding...
            `;
        }

        try {
            const result = await this.dataService.seedBankData();
            console.log('Seed result:', result);

            const showToast = (window as any).showToast;
            if (showToast) {
                const flowsCreated = result?.flows_created || 0;
                const stepsCreated = result?.steps_created || 0;
                showToast(`Seeded ${flowsCreated} process flows and ${stepsCreated} steps`, 'success');
            }

            // Reload topology
            await this.loadTopology();

        } catch (error) {
            console.error('Failed to seed data:', error);
            const showToast = (window as any).showToast;
            if (showToast) {
                showToast('Failed to seed topology data: ' + String(error), 'error');
            }
        } finally {
            if (seedBtn) {
                seedBtn.removeAttribute('disabled');
                seedBtn.innerHTML = originalText;
            }
        }
    }

    // ======================
    // Private Helper Methods
    // ======================

    /**
     * Navigate to a handoff target (e.g. "retail_banking__processing")
     * Closes the modal and drills into the target BU/capability node.
     */
    private navigateToHandoff(target: string): void {
        const [buId, capId] = target.split('__');
        if (!buId || !capId || !this.rootNode) return;

        const targetNode = this.findNodeByBuCap(this.rootNode, buId, capId);
        if (targetNode) {
            this.closeDetailModal();
            this.focusNode = targetNode;
            this.topologyVisualizer.render(this.focusNode);
            this.updateBreadcrumb();
        }
    }

    /**
     * Find a capability node by business unit ID and capability ID
     */
    private findNodeByBuCap(root: TopologyNode, buId: string, capId: string): TopologyNode | null {
        for (const buNode of root.children) {
            if (buNode.id === buId) {
                for (const capNode of buNode.children) {
                    if (capNode.id === `${buId}__${capId}`) return capNode;
                }
            }
        }
        return null;
    }

    /**
     * Check if the tree contains any process-flow nodes
     */
    private hasProcessFlows(node: TopologyNode): boolean {
        if (node.type === 'process-flow') return true;
        return node.children.some(child => this.hasProcessFlows(child));
    }

    /**
     * Recursively set expanded=true on all nodes so children are accessible
     */
    private expandAllNodes(node: TopologyNode): void {
        node.expanded = true;
        for (const child of node.children) {
            this.expandAllNodes(child);
        }
    }

    /**
     * Update breadcrumb with clickable path from root to focusNode.
     * Each segment is a clickable button that navigates to that node.
     */
    private updateBreadcrumb(): void {
        const breadcrumbEl = document.getElementById('topology-breadcrumb');
        if (!breadcrumbEl || !this.rootNode || !this.focusNode) return;

        // Build path from root to focusNode
        const path = this.findPathToNode(this.rootNode, this.focusNode.id);
        if (path.length === 0) return;

        breadcrumbEl.innerHTML = '';

        path.forEach((node, i) => {
            const color = DEFAULT_LEVEL_COLORS[node.level];
            const isLast = i === path.length - 1;

            // Separator
            if (i > 0) {
                const sep = document.createElement('span');
                sep.className = 'mx-1 text-gray-400';
                sep.textContent = '/';
                breadcrumbEl.appendChild(sep);
            }

            // Clickable breadcrumb segment
            const btn = document.createElement('button');
            btn.className = isLast
                ? 'font-semibold px-2 py-0.5 rounded'
                : 'text-gray-500 hover:text-gray-700 px-2 py-0.5 rounded hover:bg-gray-100';
            if (isLast) {
                btn.style.color = color;
            }
            btn.textContent = node.label;

            if (!isLast) {
                btn.addEventListener('click', () => {
                    this.focusNode = node;
                    this.topologyVisualizer.render(this.focusNode);
                    this.updateBreadcrumb();
                });
            }

            breadcrumbEl.appendChild(btn);
        });
    }

    private findPathToNode(root: TopologyNode, targetId: string): TopologyNode[] {
        if (root.id === targetId) return [root];
        for (const child of root.children) {
            const path = this.findPathToNode(child, targetId);
            if (path.length > 0) return [root, ...path];
        }
        return [];
    }

    /**
     * Render node detail HTML into a target element
     */
    private renderNodeDetail(panel: HTMLElement, node: TopologyNode): void {
        const color = DEFAULT_LEVEL_COLORS[node.level];
        const levelNames: Record<number, string> = {
            1: 'Bank',
            2: 'Business Unit',
            3: 'Capability',
            4: 'Process Flow',
            5: 'Process Flow',
        };

        let detailHtml = `
            <div class="space-y-3">
                <div>
                    <span class="inline-block px-2 py-0.5 text-xs rounded-full text-white mb-1" style="background-color: ${color}">
                        Level ${node.level}: ${levelNames[node.level]}
                    </span>
                    <h3 class="text-lg font-bold text-gray-800">${this.escapeHtml(node.label)}</h3>
                </div>
        `;

        // Metadata
        if (node.metadata && Object.keys(node.metadata).length > 0) {
            detailHtml += `<div class="space-y-1">`;
            for (const [key, value] of Object.entries(node.metadata)) {
                if (key === 'description' && value) {
                    detailHtml += `<p class="text-sm text-gray-600">${this.escapeHtml(value)}</p>`;
                } else if (value) {
                    detailHtml += `
                        <div class="flex items-center gap-2">
                            <span class="text-xs text-gray-400 w-20">${this.escapeHtml(key)}:</span>
                            <span class="text-xs text-gray-700">${this.escapeHtml(value)}</span>
                        </div>
                    `;
                }
            }
            detailHtml += `</div>`;
        }

        // Children list
        if (node.children.length > 0) {
            detailHtml += `
                <div class="pt-2 border-t">
                    <p class="text-xs text-gray-400 mb-2">${node.children.length} children:</p>
                    <div class="space-y-1 max-h-48 overflow-y-auto">
                        ${node.children.map(child => `
                            <div class="flex items-center gap-2 text-sm text-gray-600">
                                <span class="w-2 h-2 rounded-full" style="background-color: ${DEFAULT_LEVEL_COLORS[child.level]}"></span>
                                ${this.escapeHtml(child.label)}
                            </div>
                        `).join('')}
                    </div>
                </div>
            `;
        }

        detailHtml += `</div>`;
        panel.innerHTML = detailHtml;
    }

    private exportTopologyPNG(): void {
        const svgContainer = document.getElementById('topology-svg-container');
        if (!svgContainer) return;

        const svg = svgContainer.querySelector('svg');
        if (!svg) {
            (window as any).showToast?.('No topology to export', 'warning');
            return;
        }

        const svgData = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width * 2;
            canvas.height = img.height * 2;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#0f172a';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const pngUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = pngUrl;
                        a.download = `bank-topology-${Date.now()}.png`;
                        a.click();
                        URL.revokeObjectURL(pngUrl);
                    }
                });
            }
            URL.revokeObjectURL(url);
        };
        img.src = url;
    }

}
